import { Component, HostListener, Input, NgZone, OnChanges, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { SolarHistoryPoint } from '../version.service';
import { chartPalette, timeTooltipTitle, uiLocale, lineLegendLabels, dragZoom, alignedTimeTicks, visibleTimeTick } from '../shared/chart.utils';

@Component({
  selector: 'app-solar-chart',
  template: `
    <!-- .dash-modal-chart (styles.css): 55 vh in the desktop modal, the free height of the phone sheet -->
    <div class="dash-modal-chart">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              (click)="resetZoom()">{{ 'CHART_RESET' | translate }}</button>
    </div>
  `,
})
export class SolarChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  /** Deep-link id of the chart (the modal's view path, e.g. 'energie/chart'): series selection and
   *  zoom window go into the link (shared/chart-link.plugin.ts); null = not linked. */
  @Input() linkId: string | null = null;
  @Input() history: SolarHistoryPoint[] = [];
  @Input() darkMode = false;
  @Input() windowMs = 24 * 3600 * 1000;
  @Input() labelPv      = '';
  @Input() labelBattery = '';
  @Input() labelGrid    = '';
  @Input() labelTotal   = '';
  @Input() labelSoc     = '';

  zoomed = false;
  /** Phone (< 576 px, the modal is a full-screen sheet): compact legend and tooltip, fewer ticks. */
  private phone = window.innerWidth < 576;

  constructor(private readonly zone: NgZone) {}

  /** Crossing the phone breakpoint rebuilds the options (a linked zoom window is restored by the plugin). */
  @HostListener('window:resize')
  onResize(): void {
    if ((window.innerWidth < 576) !== this.phone) this.ngOnChanges();
  }

  /** Reset button: chartjs-plugin-zoom reports the reset to onZoomComplete, which drops the link's window. */
  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  /** After a drag zoom, a reset and a restored link window (Chart.js runs outside Angular). */
  private onZoomComplete(): void {
    this.zone.run(() => { this.zoomed = !!(this.chartDirective?.chart as any)?.isZoomedOrPanned?.(); });
  }

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};

  private formatW(w: number): string {
    const abs = Math.abs(w);
    return abs >= 1000 ? `${(w / 1000).toFixed(2)} kW` : `${Math.round(w)} W`;
  }

  /** Tooltip title: the time, beyond a day's window also the date (a week shows every time seven times);
   *  the UI locale is read at hover time, so it follows a language switch. */
  private tooltipTitle(): (items: any[]) => string {
    if (this.windowMs <= 26 * 3600 * 1000) return timeTooltipTitle;
    return (items) => {
      const ts = items[0]?.parsed.x;
      return ts == null ? '' : new Date(ts).toLocaleString(uiLocale(), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };
  }

  ngOnChanges(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const phone = this.phone = window.innerWidth < 576;
    const tick = phone ? { font: { size: 10 } } : {};
    const zeroLineColor = this.darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
    // x window spans the selected range so date labels show across week/month even
    // when the archive is still sparse.
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;
    // New options replace the scales: the zoom is gone until the link plugin restores its window.
    this.zoomed = false;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        // phone: the five series in ~2 legend lines, a tooltip that leaves the chart visible
        legend: { display: true, labels: { ...lineLegendLabels(textColor), ...(phone ? { padding: 6, font: { size: 11 } } : {}) } },
        tooltip: {
          ...(phone ? { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 6, boxWidth: 8, boxHeight: 8 } : {}),
          callbacks: {
            title: this.tooltipTitle(),
            label: (item) => {
              if ((item.dataset as any).yAxisID === 'y1') {
                return ` ${item.dataset.label}: ${Math.round(item.parsed.y ?? 0)} %`;
              }
              return ` ${item.dataset.label}: ${this.formatW(item.parsed.y ?? 0)}`;
            },
          },
        },
        zoom: dragZoom(() => this.onZoomComplete()),
        ...({ deepLink: { id: this.linkId } } as any),
      },
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          // ticks and their format from the visible window (a drag zoom, a restored link window), not
          // the full range: whole hours / days / months, thinned by autoSkip to maxTicksLimit
          afterBuildTicks: (scale: any) => {
            const t = alignedTimeTicks(scale.min, scale.max);
            if (t.length >= 2) scale.ticks = t.map(value => ({ value }));
          },
          ticks: {
            ...tick,
            color: textColor,
            maxTicksLimit: phone ? 4 : 9,
            maxRotation: 0,
            autoSkipPadding: 12,
            callback: function (this: any, val: number | string) { return visibleTimeTick(this.min, this.max, val); },
          },
          grid: { color: gridColor },
        },
        // `resolution` (yMinSpan plugin, shared/chart-link.plugin.ts): the power values are whole watts
        y: {
          resolution: 1,
          ticks: {
            ...tick,
            color: textColor,
            ...(phone ? { maxTicksLimit: 5 } : {}),
            callback: (val: number | string) => {
              const w = val as number;
              return Math.abs(w) >= 1000 ? `${(w / 1000).toFixed(1)} kW` : `${w} W`;
            },
          },
          grid: {
            color: (ctx: any) => ctx.tick.value === 0 ? zeroLineColor : gridColor,
          },
        } as any,
        y1: {
          resolution: 1,   // whole %
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            ...tick,
            color: textColor,
            callback: (val: number | string) => `${val} %`,
            maxTicksLimit: phone ? 5 : 6,
          },
          grid: { drawOnChartArea: false },
        } as any,
      },
      elements: {
        point: { radius: 0, hoverRadius: 3 },
        line:  { borderWidth: 1.5, tension: 0.6 },
      },
    };

    // linkKey: the dataset's stable name in the deep link (labels are translated)
    this.chartData = {
      datasets: [
        {
          label: `☀️ ${this.labelPv}`,
          linkKey: 'pv',
          data: this.history.map(p => ({ x: p.ts, y: p.pvYieldW })),
          borderColor: '#f59e0b',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔋 ${this.labelBattery}`,
          linkKey: 'battery',
          data: this.history.map(p => ({ x: p.ts, y: p.batteryDischargeW })),
          borderColor: '#22c55e',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔌 ${this.labelGrid}`,
          linkKey: 'grid',
          data: this.history.map(p => ({ x: p.ts, y: p.gridConsumptionW })),
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🏠 ${this.labelTotal}`,
          linkKey: 'total',
          data: this.history.map(p => ({ x: p.ts, y: p.totalConsumptionW })),
          borderColor: '#0d6efd',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔋 ${this.labelSoc}`,
          linkKey: 'soc',
          data: this.history.map(p => ({ x: p.ts, y: p.batteryPercent })),
          borderColor: '#a855f7',
          backgroundColor: 'transparent',
          fill: false,
          yAxisID: 'y1',
          borderDash: [4, 3],
        },
      ] as any[],
    };
  }
}
